import React, { useState } from 'react';
import { Box, Button, TextField, Stack, Alert, Typography, Select, MenuItem, InputLabel, FormControl } from '@mui/material';
import api from '../api/api';
import { Student } from '../types';

interface Props {
  schoolId: number;
  onSuccess: () => void;
}

const AddStudentForm: React.FC<Props> = ({ schoolId, onSuccess }) => {
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [images, setImages] = useState<FileList | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [profilePicture, setProfilePicture] = useState<File | null>(null);
  const [profilePreview, setProfilePreview] = useState<string | null>(null);
  const [classLevel, setClassLevel] = useState('Grade 1');
  const [studentId, setStudentId] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [parentPhone, setParentPhone] = useState('');
  const [parentContact, setParentContact] = useState('');
  const [emergencyContact, setEmergencyContact] = useState('');
  const [medicalInfo, setMedicalInfo] = useState('');
  const [enrollmentDate, setEnrollmentDate] = useState('');
  const [graduationDate, setGraduationDate] = useState('');
  const [status, setStatus] = useState('active');

  const classLevels = [
    { value: 'Pre-Primary 1', label: 'Pre-Primary 1' },
    { value: 'Pre-Primary 2', label: 'Pre-Primary 2' },
    { value: 'Grade 1', label: 'Grade 1' },
    { value: 'Grade 2', label: 'Grade 2' },
    { value: 'Grade 3', label: 'Grade 3' },
    { value: 'Grade 4', label: 'Grade 4' },
    { value: 'Grade 5', label: 'Grade 5' },
    { value: 'Grade 6', label: 'Grade 6' },
    { value: 'Grade 7', label: 'Grade 7' },
    { value: 'Grade 8', label: 'Grade 8' },
    { value: 'Grade 9', label: 'Grade 9' },
    { value: 'Grade 10', label: 'Grade 10' },
    { value: 'Grade 11', label: 'Grade 11' },
    { value: 'Grade 12', label: 'Grade 12' },
  ];

  const genderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'other', label: 'Other' },
  ];

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'graduated', label: 'Graduated' },
    { value: 'transferred', label: 'Transferred' },
  ];

  const handleProfilePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setProfilePicture(file);
    if (file) {
      setProfilePreview(URL.createObjectURL(file));
    } else {
      setProfilePreview(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!name || !images || images.length === 0) {
      setError('Name and at least one image are required.');
      return;
    }
    setLoading(true);
    const formData = new FormData();
    formData.append('name', name);
    if (notes) formData.append('notes', notes);
    formData.append('school_id', String(schoolId));
    formData.append('class_level', classLevel);
    if (studentId) formData.append('student_id', studentId);
    if (dateOfBirth) formData.append('date_of_birth', dateOfBirth);
    if (gender) formData.append('gender', gender);
    if (parentEmail) formData.append('parent_email', parentEmail);
    if (parentPhone) formData.append('parent_phone', parentPhone);
    if (parentContact) formData.append('parent_contact', parentContact);
    if (emergencyContact) formData.append('emergency_contact', emergencyContact);
    if (medicalInfo) formData.append('medical_info', medicalInfo);
    if (enrollmentDate) formData.append('enrollment_date', enrollmentDate);
    if (graduationDate) formData.append('graduation_date', graduationDate);
    formData.append('status', status);
    Array.from(images).forEach((img) => formData.append('images', img));
    try {
      const res = await api.post('/students/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const student = res.data as Student;
      // Upload profile picture if selected
      if (profilePicture) {
        const picData = new FormData();
        picData.append('file', profilePicture);
        await api.post(`/students/${student.id}/profile_picture`, picData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      setSuccess('Student added successfully!');
      // Reset form
      setName('');
      setNotes('');
      setImages(null);
      setProfilePicture(null);
      setProfilePreview(null);
      setStudentId('');
      setDateOfBirth('');
      setGender('');
      setParentEmail('');
      setParentPhone('');
      setParentContact('');
      setEmergencyContact('');
      setMedicalInfo('');
      setEnrollmentDate('');
      setGraduationDate('');
      setStatus('active');
      onSuccess();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to add student.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>Add Student</Typography>
      <Stack spacing={3}>
        {/* Basic Information */}
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
          Basic Information
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
            <TextField 
              label="Name" 
              value={name} 
              onChange={e => setName(e.target.value)} 
              required 
              fullWidth
            />
          </Box>
          <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
            <TextField 
              label="Student ID" 
              value={studentId} 
              onChange={e => setStudentId(e.target.value)} 
              fullWidth
            />
          </Box>
          <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
            <FormControl fullWidth required>
              <InputLabel>Class Level</InputLabel>
              <Select
                value={classLevel}
                label="Class Level"
                onChange={e => setClassLevel(e.target.value)}
              >
                {classLevels.map((level) => (
                  <MenuItem key={level.value} value={level.value}>{level.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={status}
                label="Status"
                onChange={e => setStatus(e.target.value)}
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
            <TextField 
              label="Date of Birth" 
              type="date"
              value={dateOfBirth} 
              onChange={e => setDateOfBirth(e.target.value)} 
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
            <FormControl fullWidth>
              <InputLabel>Gender</InputLabel>
              <Select
                value={gender}
                label="Gender"
                onChange={e => setGender(e.target.value)}
              >
                {genderOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
            <TextField 
              label="Enrollment Date" 
              type="date"
              value={enrollmentDate} 
              onChange={e => setEnrollmentDate(e.target.value)} 
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
            <TextField 
              label="Graduation Date" 
              type="date"
              value={graduationDate} 
              onChange={e => setGraduationDate(e.target.value)} 
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Box>

        {/* Parent Information */}
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
          Parent Information
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
            <TextField 
              label="Parent Email" 
              type="email"
              value={parentEmail} 
              onChange={e => setParentEmail(e.target.value)} 
              fullWidth
            />
          </Box>
          <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
            <TextField 
              label="Parent Phone" 
              value={parentPhone} 
              onChange={e => setParentPhone(e.target.value)} 
              fullWidth
            />
          </Box>
          <Box sx={{ flex: '1 1 100%' }}>
            <TextField 
              label="Additional Parent Contact" 
              value={parentContact} 
              onChange={e => setParentContact(e.target.value)} 
              fullWidth
            />
          </Box>
          <Box sx={{ flex: '1 1 100%' }}>
            <TextField 
              label="Emergency Contact" 
              value={emergencyContact} 
              onChange={e => setEmergencyContact(e.target.value)} 
              fullWidth
            />
          </Box>
        </Box>

        {/* Additional Information */}
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
          Additional Information
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ flex: '1 1 100%' }}>
            <TextField 
              label="Notes" 
              value={notes} 
              onChange={e => setNotes(e.target.value)} 
              multiline 
              rows={3}
              fullWidth
            />
          </Box>
          <Box sx={{ flex: '1 1 100%' }}>
            <TextField 
              label="Medical Information" 
              value={medicalInfo} 
              onChange={e => setMedicalInfo(e.target.value)} 
              multiline 
              rows={3}
              fullWidth
            />
          </Box>
        </Box>

        {/* Images */}
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
          Images
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
            <Button variant="contained" component="label" fullWidth>
              Upload Images
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={e => setImages(e.target.files)}
                style={{ display: 'none' }}
              />
            </Button>
            {images && <Typography variant="body2" sx={{ mt: 1 }}>{images.length} image(s) selected</Typography>}
          </Box>
          <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
            <Button variant="outlined" component="label" fullWidth>
              Profile Picture
              <input
                type="file"
                accept="image/*"
                onChange={handleProfilePictureChange}
                style={{ display: 'none' }}
              />
            </Button>
            {profilePreview && (
              <Box sx={{ mt: 1, textAlign: 'center' }}>
                <img 
                  src={profilePreview} 
                  alt="Profile Preview" 
                  style={{ maxWidth: '100px', maxHeight: '100px', objectFit: 'cover' }} 
                />
              </Box>
            )}
          </Box>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}

        <Button 
          type="submit" 
          variant="contained" 
          disabled={loading}
          sx={{ alignSelf: 'flex-start' }}
        >
          {loading ? 'Adding...' : 'Add Student'}
        </Button>
      </Stack>
    </Box>
  );
};

export default AddStudentForm; 